import { matchPath } from 'react-router-dom';

import { GUEST_IDS } from '../enums/UserRoleId.enum';
import { User } from '../types/User.type';

/**
 * What a Guest login (LHPH's auditors) can open. lhphcapital-7xvj.
 *
 * Kevin, 2026-10-07: "the 'Guest' login should be view-only, mainly used by our auditors …
 * It's a narrow number of screens that the read-only login can view." Blake, 2026-10-29:
 * mirror the legacy read-only site. That site is in the archive (shell SolutionsRO, 15 *_NRO
 * components), and its visible menus are:
 *
 *   Home            Dashboards
 *   Loans           Dashboard, Active Loans (with the loan's detail and scanned documents),
 *                   Paid Off Loans, Corporate Documents, Loan Calculator, Payoff Calculator
 *   Borrowing Base  Dashboard, Billing Logs, Dealer AR (ours: Current AR)
 *   Reports         Floor Plans, Credit Lines
 *
 * Legacy keeps Corporate Documents under Loans; ours is a Dealers screen, so the Guest menu
 * shows Dealers with that one item. Legacy's read-only top menu also renders buttons for
 * Dealers, Floorplans, Certs, Imports and Settings that open the normal staff screens; whether
 * they were hidden at run time is not in the archive, so they are left out (asked of Kevin).
 *
 * ⚠️ UX, not security. The server refuses every change from a Guest (guestReadOnly.js).
 */

export const isGuest = (user: User | null | undefined): boolean =>
  !!user && GUEST_IDS.has(Number(user.role_id));

/** Where a Guest lands when it opens anything else. */
export const GUEST_HOME = '/home/dashboards';

/**
 * Every page a Guest may open, as exact route patterns. The section roots are dealer pickers;
 * a detail page is listed on its own, so an action page beneath it (…/repo-swap) is not.
 */
export const GUEST_PATHS: readonly string[] = Object.freeze([
  '/home/dashboards',
  '/dealers',
  '/dealers/:id/corporate-docs',
  '/loans',
  '/loans/:id/dashboard',
  '/loans/:id/active-loans',
  '/loans/:id/active-loans/detail/:loanId',
  '/loans/:id/paid-off-loans',
  '/loans/:id/paid-off-loans/detail/:loanId',
  '/loans/:id/loan-calculator',
  '/loans/:id/payoff-calculator',
  '/borrowing-base',
  '/borrowing-base/:id/dashboard',
  '/borrowing-base/:id/current-ar',
  '/borrowing-base/:id/billing-logs',
  '/reports/floor-plans',
  '/reports/credit-lines',
  '/unauthorized',
  '/logout',
]);

export const isGuestPath = (pathname: string): boolean =>
  GUEST_PATHS.some(pattern => matchPath({ path: pattern, end: true }, pathname) !== null);

/** The Guest's menu: the legacy read-only site's, in its order. No dealer chosen, no sub-items. */
export const guestMenu = (id: string) => {
  const section = (name: string, path: string, routes: { name: string; path: string }[]) =>
    id ? { name, path, 'data-test': path, routes } : { name, path, 'data-test': path };
  return [
    {
      name: 'Home',
      path: '/home',
      'data-test': '/home',
      routes: [{ name: 'Dashboards', path: '/home/dashboards' }],
    },
    section('Dealers', '/dealers', [
      { name: 'Corporate Documents', path: `/dealers/${id}/corporate-docs` },
    ]),
    section('Loans', '/loans', [
      { name: 'Dashboard', path: `/loans/${id}/dashboard` },
      { name: 'Active Loans', path: `/loans/${id}/active-loans` },
      { name: 'Paid Off Loans', path: `/loans/${id}/paid-off-loans` },
      { name: 'Loan Calculator', path: `/loans/${id}/loan-calculator` },
      { name: 'Payoff Calculator', path: `/loans/${id}/payoff-calculator` },
    ]),
    section('Borrowing Base', '/borrowing-base', [
      { name: 'Dashboard', path: `/borrowing-base/${id}/dashboard` },
      { name: 'Current AR', path: `/borrowing-base/${id}/current-ar` },
      { name: 'Billing Logs', path: `/borrowing-base/${id}/billing-logs` },
    ]),
    {
      name: 'Reports',
      path: '/reports',
      'data-test': '/reports',
      routes: [
        { name: 'Floor Plans', path: '/reports/floor-plans' },
        { name: 'Credit Lines', path: '/reports/credit-lines' },
      ],
    },
    { name: 'Logout', path: '/logout' },
  ];
};
